import React, { useState } from 'react';
import { SxProps, Theme, Tooltip, Typography, TypographyProps } from '@mui/material';

// One line (or `lines` lines) with an ellipsis; the full text shows in a tooltip only when it is cut. `focusable`
// lets the keyboard reach a cut text so its tooltip opens on focus; a touch opens it as well.
export default function EllipsisText({
    text,
    lines = 1,
    variant,
    focusable = false,
    sx,
    dataTest,
}: {
    text: string;
    lines?: number;
    variant?: TypographyProps['variant'];
    focusable?: boolean;
    sx?: SxProps<Theme>;
    dataTest?: string;
}) {
    const [truncated, setTruncated] = useState(false);
    const measure = (event: React.SyntheticEvent<HTMLElement>) => {
        const element = event.currentTarget;
        setTruncated(element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
    };
    return (
        <Tooltip
            title={truncated ? text : ''}
            enterDelay={400}
            slotProps={{ tooltip: { sx: { maxHeight: '50vh', overflowY: 'auto' } } }}
        >
            <Typography
                variant={variant}
                noWrap={1 === lines}
                tabIndex={focusable ? 0 : undefined}
                onMouseEnter={measure}
                onFocus={measure}
                onTouchStart={measure}
                sx={[
                    { minWidth: 0, flex: '1 1 auto' },
                    lines > 1 && {
                        display: '-webkit-box',
                        WebkitLineClamp: lines,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
                data-test={dataTest}
            >
                {text}
            </Typography>
        </Tooltip>
    );
}
